import { yupResolver } from '@hookform/resolvers/yup';
import { IonButton, useIonPopover } from '@ionic/react';
import { Editor } from '@tiptap/react';
import { ComponentProps, FormEvent, MouseEvent, ReactNode, useCallback, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useIntl } from 'react-intl';
import Select from 'react-select';
import * as yup from 'yup';

import ActionIconButton from '@components/buttons/ActionIconButton';
import FormButtons from '@components/fields/FormButtons';
import Input from '@components/fields/Input';
import styles from '@components/fields/RichTextAreaMenu.module.css';
import Form from '@components/forms/Form';
import Icon from '@components/text/Icon';
import { Opener } from '@hooks/useOpener';
import { editorSelectStyles } from '@utils/formUtils';
import { IconName } from '@utils/iconUtils';
import { clickCurrentTarget } from '@utils/keyboardUtils';
import { isMobileWeb, isNativeApp } from '@utils/platformUtils';

type Props = {
  controlsOpenerMobile: Opener;
  disabled?: boolean;
  editor: Editor;
  expertLinks?: ReactNode;
  extraMenuButtons?: ReactNode[];
  forMessaging?: boolean;
  textControlsHidden: boolean;
};

type PopoverLinkFormProps = {
  editor: Editor;
  onDismiss: () => void;
  popoverDisplayed: boolean;
};

type MenuButtonProps = ComponentProps<typeof IonButton> & {
  ariaLabelKey: string;
  className: string;
  disabled?: boolean;
  forMessaging?: boolean;
  icon: IconName;
};

export const MenuButton = ({
  ariaLabelKey,
  className,
  disabled = false,
  forMessaging = false,
  icon,
  ...buttonProps
}: MenuButtonProps) => {
  const intl = useIntl();

  if (forMessaging) {
    return (
      <div className={styles.messagingIconSpacing}>
        <ActionIconButton
          active={className.includes(styles.active)}
          ariaLabelKey={ariaLabelKey}
          color="neutral500"
          disabled={disabled}
          hoverContent={intl.formatMessage({ id: ariaLabelKey })}
          icon={icon}
          onClick={buttonProps.onClick}
          tabIndex={disabled ? -1 : 0}
        />
      </div>
    );
  }

  return (
    <IonButton
      className={`${className} ${styles.button}`}
      disabled={disabled}
      tabIndex={disabled ? -1 : 0}
      {...buttonProps}
      onKeyUp={clickCurrentTarget}
    >
      <Icon
        ariaLabelKey={ariaLabelKey}
        className={styles.icon}
        hoverContent={intl.formatMessage({ id: ariaLabelKey })}
        icon={icon}
      />
    </IonButton>
  );
};

const PopoverLinkForm = ({ editor, onDismiss, popoverDisplayed }: PopoverLinkFormProps) => {
  const intl = useIntl();

  type FormData = {
    linkUrl: string;
  };

  const initialValues: FormData = {
    linkUrl: ''
  };

  const schema = yup
    .object()
    .shape({
      linkUrl: yup
        .string()
        .url(intl.formatMessage({ id: 'errors.richTextArea.linkUrl.formatting' }))
        .required(intl.formatMessage({ id: 'errors.richTextArea.linkUrl.blank' }))
    })
    .required();

  const form = useForm<FormData>({
    defaultValues: initialValues,
    mode: 'onChange',
    resolver: yupResolver(schema)
  });

  const handleSubmit = useCallback(
    (event: FormEvent<HTMLFormElement>) => {
      event.preventDefault();
      event.stopPropagation();

      editor
        .chain()
        .focus()
        .extendMarkRange('link')
        .setLink({ href: form.getValues().linkUrl, target: '_blank' })
        .run();

      onDismiss();
      editor.commands.focus();
    },
    [editor, form, onDismiss]
  );

  return (
    <div className={styles.popover}>
      <Form hookForm={form} onSubmit={handleSubmit}>
        <Input<FormData>
          autofocus={popoverDisplayed}
          control={form.control}
          error={form.formState.errors.linkUrl}
          label={intl.formatMessage({ id: 'forms.richTextArea.linkUrlLabel' })}
          name="linkUrl"
          required
        />
        <FormButtons hookForm={form} onCancel={onDismiss} saveKey="dictionary.add" />
      </Form>
    </div>
  );
};

const RichTextAreaMenu = ({
  controlsOpenerMobile,
  disabled,
  editor,
  expertLinks,
  extraMenuButtons = [],
  forMessaging = false,
  textControlsHidden
}: Props) => {
  const [linkPopoverDisplayed, setLinkPopoverDisplayed] = useState<boolean>(false);
  const [linkButtonDisabled, setLinkButtonDisabled] = useState<boolean>(true);
  const [headingSelectValue, setHeadingSelectValue] = useState(0);

  const [presentLinkPopover, dismissLinkPopover] = useIonPopover(PopoverLinkForm, {
    editor,
    onDismiss: () => dismissLinkPopover(),
    popoverDisplayed: linkPopoverDisplayed
  });

  const handleBold = useCallback(() => {
    editor.chain().focus().toggleBold().run();
  }, [editor]);

  const handleItalic = useCallback(() => {
    editor.chain().focus().toggleItalic().run();
  }, [editor]);

  const handleUnderline = useCallback(() => {
    editor.chain().focus().toggleUnderline().run();
  }, [editor]);

  const handleUnorderedList = useCallback(() => {
    editor.chain().focus().toggleBulletList().run();
  }, [editor]);

  const handleOrderedList = useCallback(() => {
    editor.chain().focus().toggleOrderedList().run();
  }, [editor]);

  const handleLink = useCallback(
    (event: MouseEvent) => {
      if (editor.isActive('link')) {
        editor.chain().focus().extendMarkRange('link').unsetLink().run();
      } else {
        presentLinkPopover({
          event: event.nativeEvent,
          onDidDismiss: () => {
            editor.commands.focus();
          },
          onDidPresent: () => {
            setLinkPopoverDisplayed(true);
          },
          onWillDismiss: () => {
            setLinkPopoverDisplayed(false);
          }
        });
      }
    },
    [editor, presentLinkPopover]
  );

  const handleHeading = useCallback(
    (value: number) => {
      setHeadingSelectValue(value);

      if (value === 0) {
        editor.chain().focus().setParagraph().run();
      } else if (value === 1 || value === 2 || value === 3) {
        editor.chain().focus().toggleHeading({ level: value }).run();
      }
    },
    [editor]
  );

  const headingSelectValues = [
    { label: 'Normal', value: 0 },
    { label: 'H2', value: 2 },
    { label: 'H3', value: 3 }
  ];

  useMemo(() => {
    editor.on('selectionUpdate', () => {
      setLinkButtonDisabled(!editor.isActive('link') && editor.view.state.selection.empty);

      if (headingSelectValue !== 0 && editor.isActive('heading', { level: 1 })) {
        setHeadingSelectValue(1);
      } else if (headingSelectValue !== 2 && editor.isActive('heading', { level: 2 })) {
        setHeadingSelectValue(2);
      } else if (headingSelectValue !== 3 && editor.isActive('heading', { level: 3 })) {
        setHeadingSelectValue(3);
      }
      if (headingSelectValue !== 0 && !editor.isActive('heading')) {
        setHeadingSelectValue(0);
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [headingSelectValue]);

  const isMobile = isNativeApp() || isMobileWeb();

  return (
    <div
      className={`${styles.menuFlex} ${forMessaging ? styles.messagingSpacing : ''}`}
      onClick={e => e.stopPropagation()}
    >
      {!textControlsHidden && (
        <>
          {isMobile && forMessaging && !controlsOpenerMobile.isOpen ? (
            <MenuButton
              ariaLabelKey="accessibility.ariaLabels.bold"
              className={editor.isActive('bold') ? styles.active : styles.inactive}
              disabled={disabled}
              forMessaging={forMessaging}
              icon="chevronRight"
              onClick={controlsOpenerMobile.open}
            />
          ) : (
            <>
              <MenuButton
                ariaLabelKey="accessibility.ariaLabels.bold"
                className={editor.isActive('bold') ? styles.active : styles.inactive}
                disabled={disabled}
                forMessaging={forMessaging}
                icon="bold"
                onClick={handleBold}
              />
              {!forMessaging && (
                <MenuButton
                  ariaLabelKey="accessibility.ariaLabels.italic"
                  className={editor.isActive('italic') ? styles.active : styles.inactive}
                  disabled={disabled}
                  forMessaging={forMessaging}
                  icon="italic"
                  onClick={handleItalic}
                />
              )}
              <MenuButton
                ariaLabelKey="accessibility.ariaLabels.underline"
                className={editor.isActive('underline') ? styles.active : styles.inactive}
                disabled={disabled}
                forMessaging={forMessaging}
                icon="underline"
                onClick={handleUnderline}
              />
              <MenuButton
                ariaLabelKey="accessibility.ariaLabels.bulletList"
                className={editor.isActive('bulletList') ? styles.active : styles.inactive}
                disabled={disabled}
                forMessaging={forMessaging}
                icon="listUl"
                onClick={handleUnorderedList}
              />
              {!forMessaging && (
                <MenuButton
                  ariaLabelKey="accessibility.ariaLabels.orderedList"
                  className={editor.isActive('orderedList') ? styles.active : styles.inactive}
                  disabled={disabled}
                  forMessaging={forMessaging}
                  icon="listOl"
                  onClick={handleOrderedList}
                />
              )}
              <div className={styles.divider} />
              <MenuButton
                ariaLabelKey="accessibility.ariaLabels.link"
                className={editor.isActive('link') ? styles.active : styles.inactive}
                disabled={disabled || linkButtonDisabled}
                forMessaging={forMessaging}
                icon="link"
                onClick={handleLink}
              />

              {extraMenuButtons.map(element => element)}
            </>
          )}

          {!forMessaging && (
            <div className={styles.select}>
              <Select
                isDisabled={disabled}
                isSearchable={false}
                menuPlacement="top"
                menuPortalTarget={document.body}
                onChange={option => handleHeading(option?.value ?? 0)}
                options={headingSelectValues}
                styles={editorSelectStyles}
                tabIndex={disabled ? -1 : 0}
                value={headingSelectValues.find(option => option.value === headingSelectValue)}
              />
            </div>
          )}
        </>
      )}
      <div className={styles.select}>{expertLinks}</div>
    </div>
  );
};

export default RichTextAreaMenu;
